import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import { resolveMemberUserLabel } from './select';
import type { MemberInput } from './types';

export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (params: api.MemberListParams) => [...memberKeys.lists(), params] as const,
  roles: () => ['roles'] as const,
  userLabel: (userId: string) => [...memberKeys.all, 'user-label', userId] as const,
};

/** Display name for a member's user id (a picker's value) — for summaries like
 *  "Owner: Ann" outside the picker itself. */
export function useMemberUserLabel(userId: string) {
  return useQuery({
    queryKey: memberKeys.userLabel(userId),
    queryFn: () => resolveMemberUserLabel(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

export function useMembers(params: api.MemberListParams = {}, enabled = true) {
  return useQuery({
    queryKey: memberKeys.list(params),
    queryFn: () => api.listMembers(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: memberKeys.roles(),
    queryFn: () => api.listRoles(),
    // Roles are global + rarely change — cache generously.
    staleTime: 5 * 60 * 1000,
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MemberInput) => api.inviteMember(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberKeys.lists() });
      toast.success('Member added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.resendInvite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberKeys.lists() });
      toast.success('Invitation resent');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetMemberWhiteLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, whiteLabel }: { id: number; whiteLabel: boolean }) =>
      api.setMemberWhiteLabel(id, whiteLabel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberKeys.lists() });
      toast.success('Updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetMemberActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api.setMemberActive(id, isActive),
    onSuccess: (_data, { isActive }) => {
      qc.invalidateQueries({ queryKey: memberKeys.lists() });
      toast.success(isActive ? 'Member reactivated' : 'Member deactivated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetMemberTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tags }: { id: number; tags: string[] }) => api.setMemberTags(id, tags),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberKeys.lists() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
